import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  getPendingUsers,
  approveUser,
  getAnalytics,
  getAllUsers,
  getUserCount
} from '../services/firestoreService';
import AnnouncementForm from './admin/AnnouncementForm';

export default function AdminDashboard() {
  const { hasRole, userProfile } = useAuth();
  const navigate = useNavigate();

  // Debug logging
  console.log('🔍 Admin Dashboard - User Profile:', userProfile);
  console.log('🔍 Admin Dashboard - Has Admin Role:', hasRole('admin'));
  const [pendingUsers, setPendingUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const isAdmin = hasRole('admin');

  useEffect(() => {
    console.log('🔍 AdminDashboard useEffect - isAdmin:', isAdmin);
    if (!isAdmin) return;
    async function fetchAdminData() {
      try {
        console.log('🔍 Fetching admin data...');

        // First check if there are any users at all
        const userCount = await getUserCount();
        console.log('🔍 Total users in database:', userCount);

        const [pending, stats, users] = await Promise.all([
          getPendingUsers().catch(err => {
            console.error('❌ Error fetching pending users:', err);
            return [];
          }),
          getAnalytics().catch(err => {
            console.error('❌ Error fetching analytics:', err);
            return { activeMembers: 0, pendingMembers: 0, totalEvents: 0, totalGroups: 0 };
          }),
          getAllUsers(100).catch(err => {
            console.error('❌ Error fetching all users:', err);
            return [];
          })
        ]);

        console.log('🔍 Admin data fetched:', {
          pending: pending.length,
          stats,
          users: users.length,
          userCount
        });

        setPendingUsers(pending);
        setAnalytics(stats);
        setAllUsers(users);
      } catch (error) {
        console.error('❌ Error fetching admin data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchAdminData();
  }, [isAdmin]);

  // Check if user is admin
  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          You don't have permission to access the admin dashboard.
        </div>
      </div>
    );
  }

  const handleApproveUser = async (userId) => {
    try {
      await approveUser(userId);
      setPendingUsers(prev => prev.filter(user => user.id !== userId));
      setAnalytics(prev => ({
        ...prev,
        activeMembers: prev.activeMembers + 1,
        pendingMembers: prev.pendingMembers - 1
      }));
    } catch (error) {
      console.error('Error approving user:', error);
      alert('Failed to approve user. Please try again.');
    }
  };

  // Quick Action handlers
  const handleCreateEvent = () => {
    navigate('/admin/events/create');
  };

  const handleManageGroups = () => {
    navigate('/admin/groups');
  };

  const handleSeedData = () => {
    navigate('/admin/seed-data');
  };

  const handleSendAnnouncement = () => {
    setShowAnnouncementForm(true);
  };

  const handleAnnouncementSuccess = (result) => {
    console.log('Announcement sent successfully:', result);
    setShowAnnouncementForm(false);
    // Optionally refresh analytics or show success message
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard 🔧</h1>
        <p className="mt-2 text-gray-600">
          Manage users, content, and monitor platform activity. LinkedIn Integration Added!
        </p>

        {/* Admin Notifications */}
        {pendingUsers.length > 0 && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  {pendingUsers.length} User{pendingUsers.length > 1 ? 's' : ''} Awaiting Approval
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    New user registrations need your attention.{' '}
                    <button
                      onClick={() => setActiveTab('pending')}
                      className="font-medium underline text-yellow-800 hover:text-yellow-600"
                    >
                      Review pending users
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-2 text-sm text-blue-600">
          Debug: Admin Role = {hasRole('admin') ? 'YES' : 'NO'} | User: {userProfile?.email || 'Not loaded'}
        </div>
        <div className="mt-1 text-sm text-gray-600">
          Users Loaded: {allUsers.length} | Pending: {pendingUsers.length} | Loading: {loading ? 'YES' : 'NO'}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview' },
            { id: 'pending', name: 'Pending Approvals', count: pendingUsers.length },
            { id: 'users', name: 'All Users' },
            { id: 'content', name: 'Content Management' },
            { id: 'linkedin', name: 'LinkedIn Integration 🔗' }
          ].map((tab) => {
            console.log('🔍 Rendering tab:', tab.name);
            return (
            <button
              key={tab.id}
              onClick={() => {
                console.log('🔍 Tab clicked:', tab.id);
                setActiveTab(tab.id);
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm relative ${
                activeTab === tab.id
                  ? 'border-apn-blue text-apn-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="flex items-center space-x-2">
                <span>{tab.name}</span>
                {tab.count > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {tab.count > 99 ? '99+' : tab.count}
                  </span>
                )}
              </span>
            </button>
            );
          })}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Users
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {analytics?.totalUsers || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Active Members
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {analytics?.activeMembers || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Pending Approval
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {analytics?.pendingMembers || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Events
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {analytics?.totalEvents || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Platform Statistics</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-4">Content Overview</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Jobs</span>
                      <span className="text-sm font-medium">{analytics?.totalJobs || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Projects</span>
                      <span className="text-sm font-medium">{analytics?.totalProjects || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Events</span>
                      <span className="text-sm font-medium">{analytics?.totalEvents || 0}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-4">User Status</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Approved Members</span>
                      <span className="text-sm font-medium text-green-600">{analytics?.activeMembers || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Pending Approval</span>
                      <span className="text-sm font-medium text-yellow-600">{analytics?.pendingMembers || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Approval Rate</span>
                      <span className="text-sm font-medium">
                        {(analytics?.totalUsers || 0) > 0
                          ? Math.round(((analytics?.activeMembers || 0) / (analytics?.totalUsers || 1)) * 100)
                          : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Approvals Tab */}
      {activeTab === 'pending' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Pending User Approvals ({pendingUsers.length})
            </h3>
          </div>
          <div className="overflow-hidden">
            {pendingUsers.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No pending approvals at this time.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Job Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Applied
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pendingUsers.map((user) => (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-700">
                                  {user.displayName?.charAt(0) || user.email?.charAt(0)}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.displayName}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.company}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.jobTitle}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleApproveUser(user.id)}
                            className="text-green-600 hover:text-green-900 mr-4"
                          >
                            Approve
                          </button>
                          <button className="text-red-600 hover:text-red-900">
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* All Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              All Users ({allUsers.length})
            </h3>
          </div>
          <div className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {allUsers.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-700">
                                {user.displayName?.charAt(0) || user.email?.charAt(0)}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.displayName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.status === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.role}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Content Management Tab */}
      {activeTab === 'content' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={handleCreateEvent}
                  className="w-full text-left px-4 py-2 text-sm text-apn-blue hover:bg-blue-50 rounded transition-colors"
                >
                  Create Event
                </button>
                <button
                  onClick={handleManageGroups}
                  className="w-full text-left px-4 py-2 text-sm text-apn-blue hover:bg-blue-50 rounded transition-colors"
                >
                  Manage Groups
                </button>
                <button
                  onClick={() => navigate('/admin/users')}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                >
                  👥 Manage Users
                </button>
                <button
                  onClick={() => navigate('/admin/postings')}
                  className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 rounded transition-colors"
                >
                  📋 Manage Postings
                </button>
                <button
                  onClick={() => navigate('/admin/seo')}
                  className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 rounded transition-colors"
                >
                  🔍 SEO Management
                </button>
                <button
                  onClick={() => navigate('/admin/export')}
                  className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded transition-colors"
                >
                  📊 Export Users
                </button>
                <button
                  onClick={handleSendAnnouncement}
                  className="w-full text-left px-4 py-2 text-sm text-apn-blue hover:bg-blue-50 rounded transition-colors"
                >
                  Send Announcement
                </button>
                <button
                  onClick={handleSeedData}
                  className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 rounded transition-colors"
                >
                  Create Sample Data
                </button>
                <button
                  onClick={() => navigate('/admin/event-cleanup')}
                  className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 rounded transition-colors"
                >
                  🗑️ Clean Test Events
                </button>
                <hr className="my-2" />
                <button
                  onClick={() => navigate('/linkedin/groups')}
                  className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
                >
                  LinkedIn Groups
                </button>
                <button
                  onClick={() => navigate('/linkedin/graduates')}
                  className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
                >
                  Graduate Sourcing
                </button>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Content Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Active Jobs</span>
                  <span className="text-sm font-medium">{analytics?.totalJobs || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Active Projects</span>
                  <span className="text-sm font-medium">{analytics?.totalProjects || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Upcoming Events</span>
                  <span className="text-sm font-medium">{analytics?.totalEvents || 0}</span>
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">System Health</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                  <span className="text-sm text-gray-600">Database: Online</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                  <span className="text-sm text-gray-600">Authentication: Online</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                  <span className="text-sm text-gray-600">Storage: Online</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* LinkedIn Integration Tab */}
      {activeTab === 'linkedin' && (
        <div className="space-y-6">
          {/* LinkedIn Integration Header */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">LinkedIn Integration</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Manage LinkedIn professional groups and graduate sourcing for talent acquisition
                </p>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                <span className="text-sm text-gray-600">Demo Mode</span>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Configuration Required</h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    LinkedIn integration requires API configuration and approval from LinkedIn.
                    Current interface shows planned features for talent sourcing and professional networking.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* LinkedIn Groups */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <svg className="h-6 w-6 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  <h4 className="text-md font-medium text-gray-900">Professional Groups</h4>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Access and manage LinkedIn professional groups for networking and talent sourcing.
                </p>
                <button
                  onClick={() => navigate('/linkedin/groups')}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
                >
                  Manage Groups
                </button>
              </div>

              {/* Graduate Sourcing */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <svg className="h-6 w-6 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                  <h4 className="text-md font-medium text-gray-900">Graduate Sourcing</h4>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Search and connect with recent finance graduates for talent acquisition.
                </p>
                <button
                  onClick={() => navigate('/linkedin/graduates')}
                  className="w-full bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
                >
                  Source Graduates
                </button>
              </div>
            </div>
          </div>

          {/* LinkedIn Statistics */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">LinkedIn Integration Stats</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">12</div>
                <div className="text-sm text-gray-600">Professional Groups</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">150+</div>
                <div className="text-sm text-gray-600">Recent Graduates</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">25k+</div>
                <div className="text-sm text-gray-600">Network Reach</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Announcement Form Modal */}
      {showAnnouncementForm && (
        <AnnouncementForm
          onClose={() => setShowAnnouncementForm(false)}
          onSuccess={handleAnnouncementSuccess}
        />
      )}
    </div>
  );
}
